// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: finds an action chip's quote inside a rendered message. The message's
// text is split across text nodes (mentions, links, bold), and the model's
// quote may differ in case and spacing, so the search runs over the joined,
// normalised text and maps the hit back to node offsets. Pure over strings so
// it is tested without a DOM; quoteRangeIn wraps it for a real element.

export interface QuoteSpan {
	startPiece: number;
	startOffset: number;
	endPiece: number;
	/** Exclusive. */
	endOffset: number;
}

const QUOTE_MARKS = /[‘’“”]/g;

function fold(char: string): string {
	return char.replace(QUOTE_MARKS, (mark) => (mark === '‘' || mark === '’' ? "'" : '"')).toLowerCase();
}

/** Where `quote` starts and ends across `pieces` (consecutive text nodes), or null. */
export function locateQuote(pieces: ReadonlyArray<string>, quote: string): QuoteSpan | null {
	const wanted = fold(quote).replace(/\s+/g, ' ').trim();
	if (!wanted) return null;
	// The joined text, whitespace runs collapsed, with each kept character's origin.
	let normal = '';
	const origin: Array<[number, number]> = [];
	let lastSpace = true;
	pieces.forEach((piece, pieceIndex) => {
		for (let offset = 0; offset < piece.length; offset += 1) {
			const char = piece[offset]!;
			if (/\s/.test(char)) {
				if (lastSpace) continue;
				normal += ' ';
				lastSpace = true;
			} else {
				normal += fold(char);
				lastSpace = false;
			}
			origin.push([pieceIndex, offset]);
		}
	});
	const at = normal.indexOf(wanted);
	if (at < 0) return null;
	const [startPiece, startOffset] = origin[at]!;
	const [endPiece, endOffset] = origin[at + wanted.length - 1]!;
	return {startPiece, startOffset, endPiece, endOffset: endOffset + 1};
}

/** A DOM Range over the quote inside `root`, or null when it is not there verbatim. */
export function quoteRangeIn(root: Element, quote: string | null | undefined): Range | null {
	if (!quote?.trim()) return null;
	const doc = root.ownerDocument;
	const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const nodes: Array<Text> = [];
	for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
	const span = locateQuote(
		nodes.map((node) => node.data),
		quote,
	);
	if (!span) return null;
	const range = doc.createRange();
	range.setStart(nodes[span.startPiece]!, span.startOffset);
	range.setEnd(nodes[span.endPiece]!, span.endOffset);
	return range;
}
